/**
 * Chat encryption for Rocky rooms (AES-256-CBC, 16-byte IV prepended to the
 * ciphertext, base64 on the wire — the same format iOS produces).
 *
 * Two key models:
 * - Public-DEK rooms (curated community/GROUP rooms): Rocky serves the raw
 *   room key as `publicDEKBase64` on the room object; import it directly
 *   (handleImportRawDEK / importRoomDek). No KEK or membership involved.
 * - Membership-DEK rooms (DMs): the user's room DEK arrives wrapped with
 *   their KEK; unwrap with handleDecryptDEK.
 *
 * Runtime constraint: this module must stay on Web Crypto via `globalThis`
 * (never `window.*`, never `node:*`). It runs in browsers, the Node
 * serverless runtime, and the Vercel workflow-sandbox bundle (reached from
 * Pulse agent tool executors), which rejects Node.js modules at build time
 * and resolves even dynamic imports.
 */

function encodeBase64(buff: Uint8Array): string {
  return globalThis.btoa(String.fromCharCode.apply(null, Array.from(buff)));
}

function decodeBase64(base64Str: string): Uint8Array {
  return Uint8Array.from(globalThis.atob(base64Str), (c) => c.charCodeAt(0));
}

async function encryptAES256CBCIV(
  key: CryptoKey,
  dataBuff: Uint8Array
): Promise<Uint8Array> {
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const encDataBuff = await globalThis.crypto.subtle.encrypt(
    { name: "AES-CBC", iv },
    key,
    dataBuff
  );

  return new Uint8Array([...iv, ...new Uint8Array(encDataBuff)]);
}

async function decryptAES256CBCIV(
  key: CryptoKey,
  encDataBuff: Uint8Array
): Promise<ArrayBuffer> {
  const iv = encDataBuff.slice(0, 16);
  return await globalThis.crypto.subtle.decrypt(
    { name: "AES-CBC", iv },
    key,
    encDataBuff.slice(16)
  );
}

async function buildKey(keyBuff: Uint8Array): Promise<CryptoKey> {
  return await globalThis.crypto.subtle.importKey(
    "raw",
    keyBuff,
    { name: "AES-CBC", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export const handleEncryptData = async ({
  DEK,
  text,
}: {
  DEK: CryptoKey | null | undefined;
  text: string;
}): Promise<string> => {
  try {
    if (!DEK) return text;
    const textBuff = new TextEncoder().encode(text);
    const roomEncTextBuff = await encryptAES256CBCIV(DEK, textBuff);

    return encodeBase64(roomEncTextBuff);
  } catch (error) {
    console.error("Error at handleEncryptData:", error);
    throw error;
  }
};

// Import a raw (unwrapped) DEK directly as an AES-CBC key.
// Used for rooms with a public DEK (publicDEKBase64 on the Rocky room object),
// where the key is served as-is and no KEK decryption is involved.
export const handleImportRawDEK = async ({
  DEKBase64,
}: {
  DEKBase64: string | null | undefined;
}): Promise<CryptoKey | null> => {
  if (!DEKBase64) return null;
  const DEKBuff = decodeBase64(DEKBase64);
  return await buildKey(DEKBuff);
};

// With a falsy KEK this echoes encDEKBase64 back unchanged (string, NOT a
// CryptoKey) — it is not a substitute for handleImportRawDEK.
export const handleDecryptDEK = async ({
  KEKBase64,
  encDEKBase64,
}: {
  KEKBase64: string | null | undefined;
  encDEKBase64: string | null | undefined;
}): Promise<CryptoKey | string | null | undefined> => {
  if (!KEKBase64) return encDEKBase64;
  if (!encDEKBase64) return null;
  const userKEKBuff = decodeBase64(KEKBase64);
  const userKEK = await buildKey(userKEKBuff);

  const encDEKBuff = decodeBase64(encDEKBase64);
  const DEKBuff = await decryptAES256CBCIV(userKEK, encDEKBuff);
  return await buildKey(new Uint8Array(DEKBuff));
};

export const handleDecryptData = async ({
  DEK,
  encTextBase64,
}: {
  DEK: CryptoKey | null | undefined;
  encTextBase64: string | null | undefined;
}): Promise<string | null | undefined> => {
  try {
    if (!DEK) return encTextBase64;
    if (!encTextBase64) return encTextBase64;
    const encTextBuff = decodeBase64(encTextBase64);
    const textBuff = await decryptAES256CBCIV(DEK, encTextBuff);
    return new TextDecoder("utf8").decode(textBuff);
  } catch (error) {
    console.error("Error at handleDecryptData:", error);
    throw error;
  }
};

/**
 * Null-semantics variants for server/agent code paths (Pulse VFS community
 * handler, chat export) where a bad key or corrupt ciphertext should degrade
 * to "no plaintext", never throw. Formerly apps/pulse/lib/community-crypto.ts.
 */

/** Import a raw base64 room DEK as an AES-CBC key. Returns null if invalid. */
export async function importRoomDek(
  dekBase64: string
): Promise<CryptoKey | null> {
  try {
    return await handleImportRawDEK({ DEKBase64: dekBase64 });
  } catch {
    return null;
  }
}

/**
 * Decrypt one message's roomEncTextBase64. Returns null on any failure. A wrong
 * key passes the CBC padding check about 1 time in 256; the strict UTF-8
 * decode rejects nearly all of those.
 */
export async function decryptRoomText(
  encTextBase64: string,
  dek: CryptoKey
): Promise<string | null> {
  try {
    const data = decodeBase64(encTextBase64);
    const plain = await decryptAES256CBCIV(dek, data);
    return new TextDecoder("utf-8", { fatal: true }).decode(plain);
  } catch {
    return null;
  }
}
